import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { Home, Plus, Trash2, Edit, Upload, X, Image as ImageIcon, FileText, Send } from 'lucide-react';
import { Footer } from '@/components/Footer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface UploadedLens {
  id: string;
  name: string;
  author: string;
  bio: string;
  produces: string[];
  score: number;
  icon_path: string | null;
  created_at: string;
  updated_at: string;
}

interface LensFile {
  id: string;
  lens_id: string;
  user_id: string;
  file_path: string;
  file_name: string;
  file_size: number;
  file_type: string | null;
  created_at: string;
}

const BecomeExpert = () => {
  const { user, loading: authLoading, isAuthor, isAuthorLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [lens, setLens] = useState<UploadedLens | null>(null);
  const [lensFiles, setLensFiles] = useState<LensFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [preparingLens, setPreparingLens] = useState(false);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [currentIconUrl, setCurrentIconUrl] = useState<string | null>(null);
  const [uploadAbortController, setUploadAbortController] = useState<AbortController | null>(null);
  const [existsInThinkingMethods, setExistsInThinkingMethods] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    author: '',
    bio: '',
    produces: '',
  });
  const [applyFormData, setApplyFormData] = useState({
    name: '',
    email: '',
    website: '',
    linkedin: '',
    bookTitle: '',
    audience: '',
  });
  const [isApplying, setIsApplying] = useState(false);
  const [isApplySubmitted, setIsApplySubmitted] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const isInitialMount = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Redirect authenticated experts to their dashboard
    if (!authLoading && !isAuthorLoading && user && isAuthor) {
      navigate('/for_expert', { replace: true });
      return;
    }

    // Public landing page: stop the loading skeleton for everyone else
    if (!authLoading && !isAuthorLoading) {
      setLoading(false);
    }

    // Only fetch lens data for authenticated experts (kept for safety; usually redirected above)
    if (user && !authLoading && !isAuthorLoading && isAuthor) {
      fetchLens();
    }
  }, [user, authLoading, isAuthor, isAuthorLoading, navigate]);

  useEffect(() => {
    if (lens?.id) {
      fetchLensFiles();
    }
  }, [lens?.id]);

  // Auto-save effect
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only auto-save if both required fields are filled
    if (!formData.name || !formData.author) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce auto-save by 1 second
    saveTimeoutRef.current = setTimeout(() => {
      handleSubmit();
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData.name, formData.author, formData.bio, formData.produces, iconFile]);

  const fetchLens = async () => {
    try {
      // Check if user has a published lens in thinking_methods (by author_email)
      console.log('[Expert] Checking thinking_methods for email:', user?.email);
      const { data: thinkingMethodData, error: tmError } = await supabase
        .from('thinking_methods')
        .select('id')
        .eq('author_email', user?.email || '')
        .limit(1);
      
      console.log('[Expert] thinking_methods result:', thinkingMethodData, 'error:', tmError);
      setExistsInThinkingMethods(thinkingMethodData && thinkingMethodData.length > 0);

      const { data, error } = await supabase
        .from('uploaded_lenses')
        .select('*')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      const lensData = data?.[0] || null;
      
      if (lensData) {
        setLens(lensData);
        setFormData({
          name: lensData.name,
          author: lensData.author,
          bio: lensData.bio || '',
          produces: lensData.produces.join(', '),
        });

        // Get signed URL for the icon if it exists
        if (lensData.icon_path) {
          const { data: urlData } = await supabase.storage
            .from('expert-lens-icons')
            .createSignedUrl(lensData.icon_path, 3600); // 1 hour expiry
          
          if (urlData?.signedUrl) {
            setCurrentIconUrl(urlData.signedUrl);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching lens:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your lens',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLensFiles = async () => {
    if (!lens?.id) return;

    try {
      const { data, error } = await supabase
        .from('lens_files')
        .select('*')
        .eq('lens_id', lens.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLensFiles(data || []);
    } catch (error) {
      console.error('Error fetching lens files:', error);
    }
  };

  const uploadIconToStorage = async (file: File, abortSignal?: AbortSignal): Promise<string> => {
    console.log('Starting icon upload to Supabase Storage...');
    console.log('File details:', { name: file.name, size: file.size, type: file.type });
    
    try {
      // Check if aborted before starting
      if (abortSignal?.aborted) {
        throw new Error('Upload cancelled');
      }

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Create unique file path: userId/timestamp-filename
      const timestamp = Date.now();
      const filePath = `${user.id}/${timestamp}-${file.name}`;
      
      console.log('Uploading to path:', filePath);

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('expert-lens-icons')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw new Error(error.message || 'Failed to upload icon');
      }

      console.log('Upload successful, path:', data.path);
      return data.path;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const cancelUpload = () => {
    if (uploadAbortController) {
      uploadAbortController.abort();
      setUploadAbortController(null);
      setUploading(false);
      
      toast({
        title: 'Upload Cancelled',
        description: 'Icon upload was cancelled',
      });
    }
  };

  const retryUpload = async () => {
    if (iconFile) {
      await handleSubmit();
    }
  };

  const handleFilesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !lens?.id || !user?.id) return;

    setUploadingFiles(true);
    const uploadedFiles: LensFile[] = [];
    let successCount = 0;
    let errorCount = 0;

    try {
      // Upload all selected files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const timestamp = Date.now();
          const filePath = `${user.id}/files/${timestamp}-${file.name}`;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('expert-lens-icons')
            .upload(filePath, file);

          if (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            errorCount++;
            continue;
          }

          // Save reference to database
          const { data, error: dbError } = await supabase
            .from('lens_files')
            .insert({
              lens_id: lens.id,
              user_id: user.id,
              file_path: filePath,
              file_name: file.name,
              file_size: file.size,
              file_type: file.type || null,
            })
            .select()
            .single();

          if (dbError) {
            console.error(`Error saving ${file.name} to database:`, dbError);
            errorCount++;
            // Clean up storage if DB insert fails
            await supabase.storage
              .from('expert-lens-icons')
              .remove([filePath]);
          } else if (data) {
            uploadedFiles.push(data);
            successCount++;
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          errorCount++;
        }
      }

      // Update the files list
      if (uploadedFiles.length > 0) {
        setLensFiles([...lensFiles, ...uploadedFiles]);
      }

      // Show appropriate toast message
      if (successCount > 0 && errorCount === 0) {
        toast({ title: "Files uploaded", description: "Your files have been saved." });
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: 'Partial Upload',
          description: `Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, but ${errorCount} failed. Please try again.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Upload Failed',
          description: 'Failed to upload files. Please try again.',
          variant: 'destructive',
        });
      }

      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload files',
        variant: 'destructive',
      });
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteFile = async (fileId: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('expert-lens-icons')
        .remove([filePath]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
      }

      // Delete from database
      const { error } = await supabase
        .from('lens_files')
        .delete()
        .eq('id', fileId);

      if (error) throw error;

      setLensFiles(lensFiles.filter(f => f.id !== fileId));
      toast({
        title: 'Success',
        description: 'File deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive',
      });
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('expert-lens-icons')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file',
        variant: 'destructive',
      });
    }
  };

  const handlePrepareLens = async () => {
    if (!lens?.id || preparingLens) return;
    setPreparingLens(true);
    try {
      const { data, error } = await supabase.functions.invoke('prepare-lens', { body: { lensId: lens.id } });
      if (error || !data?.success) throw error ?? new Error('Preparation failed');
      toast({ title: 'Success', description: 'Your lens preparation request has been submitted.', duration: 11000 });
    } catch {
      toast({ title: 'Could not submit request', description: 'Please try again later.', variant: 'destructive' });
    } finally { setPreparingLens(false); }
  };

  const handleNotifyReady = async () => {
    if (!lens?.id) return;

    setSendingNotification(true);
    try {
      const { error } = await supabase.functions.invoke('notify-lens-ready', {
        body: { lensId: lens.id }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Administrators have been notified that your lens is ready',
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to send notification',
        variant: 'destructive',
      });
    } finally {
      setSendingNotification(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.author) {
      toast({
        title: 'Validation Error',
        description: 'Name and Author are required fields',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    let uploadSuccessful = false;
    
    try {
      let iconPath = lens?.icon_path || null;
      
      if (iconFile) {
        setUploading(true);
        const abortController = new AbortController();
        setUploadAbortController(abortController);
        
        try {
          iconPath = await uploadIconToStorage(iconFile, abortController.signal);
          uploadSuccessful = true;
        } catch (uploadError) {
          setUploading(false);
          setUploadAbortController(null);
          
          if (uploadError instanceof Error && uploadError.message === 'Upload cancelled') {
            throw uploadError;
          }
          
          // Show retry option
          toast({
            title: 'Upload Failed',
            description: uploadError instanceof Error ? uploadError.message : 'Failed to upload icon',
            variant: 'destructive',
            action: (
              <ToastAction altText="Retry upload" onClick={retryUpload}>
                Retry
              </ToastAction>
            ),
          });
          throw uploadError;
        } finally {
          setUploading(false);
          setUploadAbortController(null);
        }
      }

      const producesArray = formData.produces
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

      const lensData = {
        name: formData.name,
        author: formData.author,
        bio: formData.bio,
        produces: producesArray,
        score: 4.5,
        icon_path: iconPath,
        user_id: user?.id,
      };

      if (lens) {
        const { data, error } = await supabase
          .from('uploaded_lenses')
          .update(lensData)
          .eq('id', lens.id)
          .select()
          .single();

        if (error) throw error;

        setLens(data);
      } else {
        const { data, error } = await supabase
          .from('uploaded_lenses')
          .insert([lensData])
          .select()
          .single();

        if (error) throw error;

        setLens(data);
      }

      // Refresh lens files list
      await fetchLensFiles();
      if (iconPath) {
        const { data: urlData } = await supabase.storage
          .from('expert-lens-icons')
          .createSignedUrl(iconPath, 3600);
        
        if (urlData?.signedUrl) {
          setCurrentIconUrl(urlData.signedUrl);
        }
      }

      setIconFile(null);
      setIconPreview(null);
    } catch (error) {
      console.error('Error saving lens:', error);
      
      let errorMessage = 'Failed to save lens';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!lens || !user?.id) return;
    
    setDeleting(true);
    try {
      // List and delete all files in the user's directory
      const { data: fileList, error: listError } = await supabase.storage
        .from('expert-lens-icons')
        .list(user.id);

      if (listError) {
        console.error('Error listing files:', listError);
      } else if (fileList && fileList.length > 0) {
        // Delete all files in the user's directory
        const filePaths = fileList.map(file => `${user.id}/${file.name}`);
        const { error: storageError } = await supabase.storage
          .from('expert-lens-icons')
          .remove(filePaths);
        
        if (storageError) {
          console.error('Error deleting files:', storageError);
        } else {
          console.log(`Deleted ${filePaths.length} file(s) from storage`);
        }
      }

      // Delete lens from database
      const { error } = await supabase
        .from('uploaded_lenses')
        .delete()
        .eq('id', lens.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Lens and all associated files deleted successfully',
      });

      // Reset form
      setLens(null);
      setCurrentIconUrl(null);
      setFormData({
        name: '',
        author: '',
        bio: '',
        produces: '',
      });
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting lens:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete lens',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleApplySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsApplying(true);
    setIsAlreadyRegistered(false);

    try {
      const { data, error } = await supabase.functions.invoke('submit-waitlist', {
        body: {
          name: applyFormData.name,
          email: applyFormData.email,
          userType: 'expert',
          website: applyFormData.website,
          linkedin: applyFormData.linkedin,
          bookTitle: applyFormData.bookTitle,
          methodType: '',
          audience: applyFormData.audience,
        },
      });

      if (error) throw error;

      const alreadyRegistered = Boolean((data as { alreadyRegistered?: boolean } | null)?.alreadyRegistered);
      setIsApplySubmitted(true);
      setIsAlreadyRegistered(alreadyRegistered);

      if (!alreadyRegistered) {
        toast({
          title: 'Success!',
          description: "You've been added to the Applyt experts list. We'll review your application and reach out.",
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit. Please try again.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };


  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-6 py-12">


          <div className="mb-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              My Expert Lens
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Scale your expertise and start earning from its daily use.
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-base leading-7 text-muted-foreground">
                Fill out this short form to tell us about your framework and audience. Our team will review fit and reach out with next steps for the founding experts cohort.
              </p>
              <p className="text-base leading-7 text-muted-foreground">
                This form is an expression of interest. It does not obligate you to anything. We will review fit and follow up by email.
              </p>
            </div>

            <Card>
              <CardContent className="p-6">
                {!isApplySubmitted ? (
                  <form onSubmit={handleApplySubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-name">
                        Full name<span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id="apply-name"
                        type="text"
                        placeholder="Your name"
                        value={applyFormData.name}
                        onChange={(e) => setApplyFormData((prev) => ({ ...prev, name: e.target.value }))}
                        required
                        disabled={isApplying}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="apply-email">
                        Email<span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id="apply-email"
                        type="email"
                        placeholder="you@example.com"
                        value={applyFormData.email}
                        onChange={(e) => setApplyFormData((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        disabled={isApplying}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="apply-website">Website</Label>
                        <Input
                          id="apply-website"
                          type="url"
                          placeholder="https://yoursite.com"
                          value={applyFormData.website}
                          onChange={(e) => setApplyFormData((prev) => ({ ...prev, website: e.target.value }))}
                          disabled={isApplying}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="apply-linkedin">
                          LinkedIn<span className="text-destructive ml-1">*</span>
                        </Label>
                        <Input
                          id="apply-linkedin"
                          type="url"
                          placeholder="https://linkedin.com/in/..."
                          value={applyFormData.linkedin}
                          onChange={(e) => setApplyFormData((prev) => ({ ...prev, linkedin: e.target.value }))}
                          required
                          disabled={isApplying}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="apply-book-title">
                        Book / method title<span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id="apply-book-title"
                        type="text"
                        placeholder="Title of your book, flagship program, or framework"
                        value={applyFormData.bookTitle}
                        onChange={(e) => setApplyFormData((prev) => ({ ...prev, bookTitle: e.target.value }))}
                        required
                        disabled={isApplying}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="apply-audience">Your current audience</Label>
                      <Textarea
                        id="apply-audience"
                        placeholder="Who uses or follows your method today? (e.g. tech teams, founders, managers, etc.)"
                        value={applyFormData.audience}
                        onChange={(e) => setApplyFormData((prev) => ({ ...prev, audience: e.target.value }))}
                        disabled={isApplying}
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    <Button type="submit" disabled={isApplying} className="w-full">
                      {isApplying ? 'Submitting...' : 'Become an Applyt Expert'}
                    </Button>
                  </form>
                ) : (
                  <div className="py-6 space-y-3">
                    <h3 className="text-xl font-semibold text-foreground">Thanks for your application!</h3>
                    <p className="text-muted-foreground">We'll be in touch soon.</p>
                    {isAlreadyRegistered && (
                      <div className="px-3 py-2 rounded-md border border-border text-sm text-muted-foreground">
                        Already registered
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lens</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this lens? This action cannot be undone.
              All files in your private storage directory will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="gap-2"
            >
              {deleting ? (
                <>
                  <Trash2 className="h-4 w-4 animate-pulse" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BecomeExpert;
